import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { XAxis, YAxis, Area, AreaChart, CartesianGrid } from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown } from "lucide-react";
import { CHART_ANIMATION } from "@/lib/chart-animation";
import { cn } from "@/lib/utils";
import type { CurrentValuationSnapshot, ValueChart, PerformanceResult } from "@/schemas";
import {
  formatChartTooltipLabel,
  formatCurrency,
  formatCompactCurrency,
  formatXAxis,
  formatYAxis,
} from "../../utils/format-utils";

interface PortfolioValueChartProps {
  chartData: ValueChart | null;
  performanceData: PerformanceResult | null;
  currentValuation: CurrentValuationSnapshot | null;
  period: string;
  onPeriodChange?: (period: string) => void;
  currencyCode: string;
  loading: {
    chart: boolean;
    performance: boolean;
  };
}

const PERIODS = ["1d", "1w", "1m", "1y", "max"];

const chartConfig = {
  price: {
    label: "Value",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

export function PortfolioValueChart({
  chartData,
  performanceData,
  currentValuation,
  period,
  onPeriodChange,
  currencyCode,
  loading,
}: PortfolioValueChartProps) {
  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (
      !performanceData ||
      !performanceData.data ||
      !performanceData.data.total_performance
    ) {
      return {
        currentValue: 0,
        change: 0,
        changePercent: 0,
        isPositive: true,
      };
    }
    const totalPerf = performanceData.data.total_performance;
    const currentValue =
      currentValuation?.total_current_value ?? totalPerf.total_current_value;
    const change = totalPerf.total_performance_absolute;
    const changePercent = totalPerf.total_performance_percentage;
    return {
      currentValue,
      change,
      changePercent,
      isPositive: change >= 0,
    };
  }, [performanceData, currentValuation]);

  // Calculate Y-axis domain
  const yAxisDomain = useMemo(() => {
    if (!chartData || chartData.value_points.length === 0) {
      return [0, 100];
    }
    const prices = chartData.value_points.map((point) => point.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const padding = (maxPrice - minPrice) * 0.1;
    return [Math.max(0, minPrice - padding), maxPrice + padding];
  }, [chartData]);

  const { currentValue, change, changePercent, isPositive } =
    performanceMetrics;
  const showPnLBreakdown = period?.toLowerCase() === "max";

  return (
    <div className="flex flex-col h-full">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-baseline gap-2 sm:gap-4 flex-wrap">
          {loading.performance ? (
            <Skeleton className="h-12 w-56 rounded-xl" />
          ) : (
            <>
              <span className="text-2xl sm:text-4xl font-bold tracking-tight">
                {formatCompactCurrency(currentValue, currencyCode)}
              </span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium cursor-help",
                        isPositive
                          ? "bg-emerald-500/10 text-emerald-600"
                          : "bg-red-500/10 text-red-600",
                      )}
                    >
                      {isPositive ? (
                        <TrendingUp className="h-4 w-4" />
                      ) : (
                        <TrendingDown className="h-4 w-4" />
                      )}
                      <span>
                        {isPositive ? "+" : ""}
                        {formatCurrency(change, currencyCode)}
                      </span>
                      <span className="opacity-70">
                        ({isPositive ? "+" : ""}
                        {changePercent.toFixed(2)}%)
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="p-3 font-mono text-xs">
                    <div className="space-y-1.5">
                      {showPnLBreakdown ? (
                        <>
                          <div className="flex justify-between gap-6">
                            <span className="text-muted-foreground uppercase text-[10px] font-bold">
                              Unrealized P&amp;L
                            </span>
                            <span
                              className={cn(
                                "font-bold",
                                (performanceData?.data.total_performance
                                  .unrealized_pnl ?? 0) >= 0
                                  ? "text-emerald-500"
                                  : "text-red-500",
                              )}
                            >
                              {(performanceData?.data.total_performance
                                .unrealized_pnl ?? 0) >= 0
                                ? "+"
                                : ""}
                              {formatCurrency(
                                performanceData?.data.total_performance
                                  .unrealized_pnl ?? 0,
                                currencyCode,
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between gap-6">
                            <span className="text-muted-foreground uppercase text-[10px] font-bold">
                              Realized P&amp;L
                            </span>
                            <span
                              className={cn(
                                "font-bold",
                                (performanceData?.data.total_performance
                                  .realized_pnl ?? 0) >= 0
                                  ? "text-emerald-500"
                                  : "text-red-500",
                              )}
                            >
                              {(performanceData?.data.total_performance
                                .realized_pnl ?? 0) >= 0
                                ? "+"
                                : ""}
                              {formatCurrency(
                                performanceData?.data.total_performance
                                  .realized_pnl ?? 0,
                                currencyCode,
                              )}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between gap-6">
                            <span className="text-muted-foreground uppercase text-[10px] font-bold">
                              Net P&amp;L
                            </span>
                            <span
                              className={cn(
                                "font-bold",
                                isPositive
                                  ? "text-emerald-500"
                                  : "text-red-500",
                              )}
                            >
                              {isPositive ? "+" : ""}
                              {formatCurrency(change, currencyCode)}
                            </span>
                          </div>
                          <div className="flex justify-between gap-6">
                            <span className="text-muted-foreground uppercase text-[10px] font-bold">
                              Net Return
                            </span>
                            <span
                              className={cn(
                                "font-bold",
                                isPositive
                                  ? "text-emerald-500"
                                  : "text-red-500",
                              )}
                            >
                              {isPositive ? "+" : ""}
                              {changePercent.toFixed(2)}%
                            </span>
                          </div>
                        </>
                      )}
                      <div className="h-px bg-border my-1" />
                      <div className="text-[9px] text-muted-foreground italic">
                        * Totals for the selected period
                        {!showPnLBreakdown
                          ? " (Realized/Unrealized breakdown shown on MAX only)"
                          : ""}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>

        {/* Period Selector */}
        {onPeriodChange && (
          <div className="inline-flex w-fit max-w-full items-center gap-1 bg-secondary/50 rounded-full p-1 border border-border/40 overflow-x-auto no-scrollbar">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => onPeriodChange(p)}
                className={cn(
                  "min-w-[2.25rem] px-2 sm:px-3 py-1 text-[10px] font-semibold rounded-full transition-all",
                  period === p
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="min-h-0 pb-3 h-[220px] sm:h-[300px] md:h-[350px] lg:h-[450px]">
        {loading.chart ? (
          <Skeleton className="h-full w-full rounded-xl" />
        ) : chartData && chartData.value_points.length > 0 ? (
          <ChartContainer
            config={chartConfig}
            className="h-full w-full min-h-[220px]"
          >
            <AreaChart
              data={chartData.value_points}
              margin={{ top: 10, left: 10, right: 20, bottom: 16 }}
            >
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0.15}
                  />
                  <stop
                    offset="95%"
                    stopColor="hsl(var(--primary))"
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.3}
                vertical={false}
              />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(ts) => formatXAxis(ts, period)}
                tick={{
                  fontSize: 10,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                domain={yAxisDomain}
                tickFormatter={(v) => formatYAxis(v, currencyCode)}
                tick={{
                  fontSize: 10,
                  fill: "hsl(var(--muted-foreground))",
                }}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={55}
              />
              <ChartTooltip
                cursor={{
                  stroke: "hsl(var(--primary))",
                  strokeWidth: 1,
                  strokeOpacity: 0.2,
                }}
                content={
                  <ChartTooltipContent
                    indicator="line"
                    labelFormatter={(value) =>
                      formatChartTooltipLabel(String(value), period)
                    }
                  />
                }
              />
              <Area
                type="monotone"
                dataKey="price"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#colorValue)"
                dot={false}
                {...CHART_ANIMATION}
              />
            </AreaChart>
          </ChartContainer>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm font-medium">
            No data available
          </div>
        )}
      </div>
    </div>
  );
}
